/**
 * frontend/src/pages/valuation/_IntakeParticulars.tsx
 *
 * VAL-ENGAGEMENT-PARTICULARS-1 / VAL-PARTICULARS-UI-1 — the ten VS Section
 * 100 engagement particulars, collected on Intake and persisted onto the
 * ENGAGEMENT itself (not the run body — these describe the engagement, not
 * one calculation of it). Split out of ValuationIntake.tsx under
 * docs/FILE_SIZE_POLICY.md ahead of landing, not after, to keep that screen
 * inside the 400-line stop.
 *
 * Standard of value drops `liquidation_value` (owner ruling 2026-09-15),
 * leaving fair market / fair / investment value.
 *
 * VAL-WIRE-CONSISTENCY-1 R2: premise of value is the engagement's own value,
 * chosen on the create form — shown here as an editable field and never
 * overwritten. `going_concern` is the default only when none was chosen.
 *
 * No client-side block: leaving a required field blank never prevents a
 * run. Only finalize refuses — see core/valuation/finalize_particulars.py.
 */
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import type { ValuationEngagement } from '@/types/valuation'

export interface ParticularsState {
  prepared_for: string
  premise_of_value: string
  effective_date: string
  purpose: string
  intended_users: string
  standard_of_value: string
  subject_interest: string
  interest_characteristics: string
  sources_relied_upon: string
  analyst_has_financial_interest: boolean
  fee_is_contingent: boolean
}

export const BLANK_PARTICULARS: ParticularsState = {
  prepared_for: '',
  premise_of_value: 'going_concern',
  effective_date: '',
  purpose: '',
  intended_users: '',
  standard_of_value: '',
  subject_interest: '',
  interest_characteristics: '',
  sources_relied_upon: '',
  analyst_has_financial_interest: false,
  fee_is_contingent: false,
}

/** Hydrate from whatever the engagement already carries — null becomes ''. */
export function particularsFromEngagement(en: ValuationEngagement): ParticularsState {
  return {
    prepared_for: en.prepared_for ?? '',
    premise_of_value: en.premise_of_value?.trim() || 'going_concern',
    effective_date: en.effective_date ?? '',
    purpose: en.purpose ?? '',
    intended_users: en.intended_users ?? '',
    standard_of_value: en.standard_of_value ?? '',
    subject_interest: en.subject_interest ?? '',
    interest_characteristics: en.interest_characteristics ?? '',
    sources_relied_upon: en.sources_relied_upon ?? '',
    analyst_has_financial_interest: en.analyst_has_financial_interest,
    fee_is_contingent: en.fee_is_contingent,
  }
}

// Owner ruling 2026-09-15: liquidation_value dropped this phase.
export const STANDARDS_OF_VALUE = [
  { value: 'fair_market_value', label: 'Fair market value' },
  { value: 'fair_value', label: 'Fair value' },
  { value: 'investment_value', label: 'Investment value' },
]

export function IntakeParticularsSection({
  particulars,
  setParticulars,
}: {
  particulars: ParticularsState
  setParticulars: (next: ParticularsState) => void
}) {
  function update(patch: Partial<ParticularsState>) {
    setParticulars({ ...particulars, ...patch })
  }

  return (
    <Card id="particulars">
      <CardHeader><CardTitle>Engagement particulars</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-gray-500">
          Required fields are needed to finalize — leaving one blank never blocks running a
          valuation.
        </p>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label htmlFor="prepared_for" className="text-xs">Prepared for</Label>
            <Input
              id="prepared_for"
              value={particulars.prepared_for}
              onChange={e => update({ prepared_for: e.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="effective_date" className="text-xs">Valuation date (effective date)</Label>
            <Input
              id="effective_date"
              type="date"
              value={particulars.effective_date}
              onChange={e => update({ effective_date: e.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="standard_of_value" className="text-xs">Standard of value *</Label>
            <select
              id="standard_of_value"
              className="h-9 w-full rounded-md border border-gray-300 bg-white px-2 text-sm"
              value={particulars.standard_of_value}
              onChange={e => update({ standard_of_value: e.target.value })}
            >
              <option value="">Select…</option>
              {STANDARDS_OF_VALUE.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <Label htmlFor="premise_of_value" className="text-xs">Premise of value *</Label>
          <Input
            id="premise_of_value"
            value={particulars.premise_of_value}
            onChange={e => update({ premise_of_value: e.target.value })}
          />
          <p className="mt-0.5 text-xs text-gray-400">
            As chosen when the engagement was created; going concern if none was chosen.
          </p>
        </div>

        <div>
          <Label htmlFor="purpose" className="text-xs">Purpose of the engagement *</Label>
          <Textarea
            id="purpose" rows={2}
            value={particulars.purpose}
            onChange={e => update({ purpose: e.target.value })}
          />
        </div>

        <div>
          <Label htmlFor="intended_users" className="text-xs">Intended users *</Label>
          <Textarea
            id="intended_users" rows={2}
            value={particulars.intended_users}
            onChange={e => update({ intended_users: e.target.value })}
          />
        </div>

        <div>
          <Label htmlFor="subject_interest" className="text-xs">Subject interest *</Label>
          <Input
            id="subject_interest"
            value={particulars.subject_interest}
            onChange={e => update({ subject_interest: e.target.value })}
            placeholder="e.g. 100% of the membership interests"
          />
        </div>

        <div>
          <Label htmlFor="interest_characteristics" className="text-xs">Interest characteristics</Label>
          <Textarea
            id="interest_characteristics" rows={2}
            value={particulars.interest_characteristics}
            onChange={e => update({ interest_characteristics: e.target.value })}
            placeholder="Control vs. minority, marketable vs. non-marketable"
          />
        </div>

        <div>
          <Label htmlFor="sources_relied_upon" className="text-xs">Sources relied upon</Label>
          <Textarea
            id="sources_relied_upon" rows={2}
            value={particulars.sources_relied_upon}
            onChange={e => update({ sources_relied_upon: e.target.value })}
          />
        </div>

        <div className="flex flex-wrap gap-6">
          <label className="flex items-center gap-2 text-sm text-gray-900">
            <input
              type="checkbox"
              checked={particulars.analyst_has_financial_interest}
              onChange={e => update({ analyst_has_financial_interest: e.target.checked })}
            />
            Analyst has a financial interest
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-900">
            <input
              type="checkbox"
              checked={particulars.fee_is_contingent}
              onChange={e => update({ fee_is_contingent: e.target.checked })}
            />
            Fee is contingent
          </label>
        </div>
      </CardContent>
    </Card>
  )
}
